/**
 * Automations panel — Phase 2 Growth Engine.
 *
 * One screen to author / list / pause / delete automation workflows. Powers
 * Lead Nurturing, Customer Nurturing, and the general Automation Center
 * (they're three filters on the same backend collection).
 *
 * The step editor is intentionally simple — a stacked list of step cards
 * with type selector + the most common config fields inline. Full
 * drag-and-drop reordering is Phase 2.1; for now reorder via ↑ / ↓ buttons.
 */
import { useEffect, useState, useCallback } from "react";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Workflow, Trash2, Play, Pause, ArrowUp, ArrowDown, Beaker, History } from "lucide-react";

const CATEGORIES = [
  { v: "lead_nurture",     l: "Lead Nurture" },
  { v: "customer_nurture", l: "Customer Nurture" },
  { v: "automation",       l: "Automation" },
];

const STEP_LIBRARY = [
  { type: "delay",            label: "Wait", fields: [["days", "Days", "number"]] },
  { type: "create_followup",  label: "Create follow-up",     fields: [["title", "Title"], ["purpose", "Purpose"], ["due_in_days", "Due in (days)", "number"]] },
  { type: "send_whatsapp",    label: "Queue WhatsApp",       fields: [["fallback_body", "Message body", "textarea"]] },
  { type: "send_email",       label: "Queue email",          fields: [["subject", "Subject"], ["body", "Body", "textarea"]] },
  { type: "notify_owner",     label: "Notify owner",         fields: [["title", "Title"], ["body", "Body"]] },
  { type: "notify_manager",   label: "Notify manager",       fields: [["title", "Title"], ["body", "Body"]] },
  { type: "set_lead_status",  label: "Set lead status",      fields: [["status", "Status"]] },
  { type: "set_lead_score",   label: "Adjust lead score",    fields: [["delta", "Score delta", "number"]] },
  { type: "tag",              label: "Add tag",              fields: [["tag", "Tag"]] },
];

const STATUS_TONE = {
  active:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  paused:  "bg-amber-50 text-amber-800 border-amber-200",
  draft:   "bg-muted/40 text-muted-foreground border-border/60",
};

function StepCard({ step, index, total, onChange, onMove, onRemove }) {
  const def = STEP_LIBRARY.find((s) => s.type === step.type) || STEP_LIBRARY[0];
  return (
    <Card className="rounded-2xl border-border/70 bg-muted/20">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-extrabold text-muted-foreground tabular-nums">#{index + 1}</span>
          <Select value={step.type} onValueChange={(v) => onChange({ ...step, type: v, config: {} })}>
            <SelectTrigger className="rounded-xl h-9 flex-1" data-testid={`wf-step-type-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-2xl">
              {STEP_LIBRARY.map((s) => <SelectItem key={s.type} value={s.type}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onMove(index, -1)} disabled={index === 0} title="Move up"><ArrowUp className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onMove(index, 1)} disabled={index === total - 1} title="Move down"><ArrowDown className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => onRemove(index)} title="Remove"><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {def.fields.map(([key, label, kind]) => (
            <div key={key}>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
              {kind === "textarea" ? (
                <Textarea
                  className="rounded-xl mt-0.5"
                  rows={2}
                  value={step.config?.[key] || ""}
                  onChange={(e) => onChange({ ...step, config: { ...(step.config || {}), [key]: e.target.value } })}
                />
              ) : (
                <Input
                  type={kind || "text"}
                  className="rounded-xl h-9 mt-0.5"
                  value={step.config?.[key] ?? ""}
                  onChange={(e) => onChange({ ...step, config: { ...(step.config || {}), [key]: kind === "number" ? Number(e.target.value) : e.target.value } })}
                />
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function WorkflowEditor({ open, onOpenChange, initial, meta, onSaved }) {
  const [form, setForm] = useState({
    name: "", description: "", category: "lead_nurture", status: "draft",
    trigger: { event: "lead.created", conditions: [] },
    steps: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      ...initial,
      trigger: initial.trigger || { event: "lead.created", conditions: [] },
      steps: initial.steps || [],
    } : {
      name: "", description: "", category: "lead_nurture", status: "draft",
      trigger: { event: "lead.created", conditions: [] },
      steps: [],
    });
  }, [open, initial]);

  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, { type: "create_followup", config: {} }] }));
  const updateStep = (i, s) => setForm((f) => ({ ...f, steps: f.steps.map((x, idx) => idx === i ? s : x) }));
  const moveStep = (i, dir) => setForm((f) => {
    const arr = [...f.steps]; const j = i + dir; if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...f, steps: arr };
  });
  const removeStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the workflow a name"); return; }
    if (!form.steps.length) { toast.error("Add at least one step"); return; }
    setSaving(true);
    try {
      if (initial?.id) await api.patch(`/automations/${initial.id}`, form);
      else             await api.post("/automations", form);
      toast.success("Workflow saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(fmtError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[760px] rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="wf-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{initial ? "Edit workflow" : "New workflow"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold">Name</Label>
              <Input className="rounded-2xl h-11 mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Welcome new leads" data-testid="wf-name" />
            </div>
            <div>
              <Label className="text-xs font-bold">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-2xl h-11 mt-1" data-testid="wf-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold">Trigger event</Label>
              <Select value={form.trigger.event} onValueChange={(v) => setForm({ ...form, trigger: { ...form.trigger, event: v } })}>
                <SelectTrigger className="rounded-2xl h-11 mt-1" data-testid="wf-trigger"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl max-h-72">
                  {(meta?.trigger_events || []).map((e) => <SelectItem key={e} value={e} className="font-mono text-xs">{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-2xl h-11 mt-1" data-testid="wf-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold">Description</Label>
              <Textarea rows={2} className="rounded-2xl mt-1" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>

          <div className="border-t border-border/60 pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Steps</p>
              <Button onClick={addStep} size="sm" variant="outline" className="rounded-xl" data-testid="wf-add-step">
                <Plus className="h-3 w-3 mr-1" /> Add step
              </Button>
            </div>
            <div className="space-y-2">
              {form.steps.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No steps yet. Add the first one to start.</p>
              ) : form.steps.map((s, i) => (
                <StepCard
                  key={i} step={s} index={i} total={form.steps.length}
                  onChange={(s2) => updateStep(i, s2)}
                  onMove={moveStep}
                  onRemove={removeStep}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="wf-save">{saving ? "Saving…" : "Save workflow"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AutomationsPanel() {
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("__all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, r] = await Promise.all([
        api.get("/automations/meta"),
        api.get("/automations"),
      ]);
      setMeta(m.data); setRows(r.data || []);
    } catch (e) { toast.error(fmtError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (wf) => {
    try {
      await api.patch(`/automations/${wf.id}`, { ...wf, status: wf.status === "active" ? "paused" : "active" });
      toast.success(`Workflow ${wf.status === "active" ? "paused" : "activated"}`);
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };
  const remove = async (wf) => {
    if (!window.confirm(`Delete "${wf.name}"?`)) return;
    try { await api.delete(`/automations/${wf.id}`); load(); }
    catch (e) { toast.error(fmtError(e)); }
  };
  const testFire = async (wf) => {
    try {
      const { data } = await api.post(`/automations/${wf.id}/test-fire`, { __kind: "lead", id: "test-lead", name: "Test Lead", owner_id: "test", phone: "+91 90000 00000", email: "test@example.com", industry: "cement" });
      toast.success(`Test fire: ${data.status} (${data.steps_executed} steps)`);
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const filtered = rows.filter((r) => categoryFilter === "__all" ? true : r.category === categoryFilter);

  return (
    <div className="space-y-4" data-testid="automations-panel">
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          <div className="flex items-center gap-2">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="rounded-2xl h-10 w-48" data-testid="wf-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All workflows</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Badge variant="outline" className="rounded-full text-xs">{filtered.length} of {rows.length}</Badge>
          </div>
          <Button onClick={() => { setEditing(null); setEditorOpen(true); }} className="rounded-2xl" data-testid="wf-new">
            <Plus className="h-4 w-4 mr-1.5" /> New workflow
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <Card className="rounded-3xl"><CardContent className="p-8 text-center text-muted-foreground">Loading…</CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card className="rounded-3xl"><CardContent className="p-10 text-center">
          <div className="h-14 w-14 rounded-3xl bg-primary/10 text-primary mx-auto grid place-items-center mb-3"><Workflow className="h-6 w-6" /></div>
          <p className="font-semibold">No workflows yet.</p>
          <p className="text-sm text-muted-foreground mt-1">Set up automations to nurture leads, win-back dormant customers, and react to CRM events.</p>
        </CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {filtered.map((wf) => (
            <Card key={wf.id} className="rounded-3xl border-border/60 shadow-soft" data-testid={`wf-card-${wf.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-display text-lg font-extrabold truncate">{wf.name}</p>
                    <p className="text-[11px] text-muted-foreground capitalize">{wf.category.replace(/_/g, " ")} · <span className="font-mono">{wf.trigger?.event}</span></p>
                  </div>
                  <Badge className={`text-[10px] capitalize border ${STATUS_TONE[wf.status] || ""}`}>{wf.status}</Badge>
                </div>
                {wf.description && <p className="text-xs text-muted-foreground line-clamp-2">{wf.description}</p>}
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span><Workflow className="h-3 w-3 inline mr-1" />{(wf.steps || []).length} steps</span>
                  <span><History className="h-3 w-3 inline mr-1" />{wf.stats?.fired || 0} fires</span>
                  {(wf.stats?.failed || 0) > 0 && <span className="text-rose-600">⚠ {wf.stats.failed} failed</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-border/60">
                  <Switch checked={wf.status === "active"} onCheckedChange={() => toggleStatus(wf)} />
                  <span className="text-xs">{wf.status === "active" ? "Active" : "Inactive"}</span>
                  <div className="flex-1" />
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => testFire(wf)} data-testid={`wf-test-${wf.id}`}><Beaker className="h-3 w-3 mr-1" />Test</Button>
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => { setEditing(wf); setEditorOpen(true); }}>Edit</Button>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-rose-600" onClick={() => remove(wf)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <WorkflowEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        initial={editing}
        meta={meta}
        onSaved={load}
      />
    </div>
  );
}
